import * as d3 from 'd3'
import {chain} from 'lodash'
import React, {useContext, useMemo} from 'react'
import {Box, styleAndCapture} from 'external/material'
import {Spring} from 'react-spring/renderprops'

import {inViewer, version, converseDisabled} from 'helpers/url'
import Icon from 'components/icon'
import createVersionComponent from 'components/VersionLabel'
import { TeacherContext } from 'teacher/context'

import MenuItem from './menu-item'
import SubMenuItem from './SubMenuItem'

const Version = createVersionComponent()
const StyledMenu = styleAndCapture(Box, 'size', 'top')(menuStyles)


const typeHandlers = {
  'submenu': SubMenuItem
}

function getHandler(type) {
  return typeHandlers[type] || MenuItem
}

function inLocation({location}, menuItem) {
  const locations = (menuItem.location || "").split(',')
  return locations.indexOf(location || 'pr') >= 0
}

const menuItems = (chained, direction, page, context) => chained
  .map(item => ({...item, direction, selected: item.page == page || (item.items && item.items.findIndex(i => i.page == page) >= 0)}))
  .map(item => {
    const Handler = getHandler(inLocation(context.profile.institution, item) && item.type)
    return <Handler key={item.order} {...item} />
  })
  .value()

const itemHeight = 90
const itemWidth = 90
const headerHeight = 50
const navigationHeight = 50

const availableHeight = ({height}) => {
  return height - headerHeight - navigationHeight
}

const visibleItemCount = (sizes, items) => {
  const available = availableHeight(sizes)
  const count = Math.floor(available / itemHeight - 1)
  return items - count < 2 ? items : count
}


export default function Menu () {
  const context = useContext(TeacherContext)
  const language = context.language
  const role = context.profile.role
  const {limited, project, premium} = context.profile.institution

  const noChat = converseDisabled || context.profile.institution.noChat

  const isDreyfous = context.profile.domain === 'dreyfous' || context.profile.domain === 'dreyfous-dg'

  const items = (context.settings.menu[project][role]).map(item => {
    return item
  }).filter(i => !i.inViewer || inViewer)
    .filter(i => i.page == 'chat' ? !noChat : true)
    .filter(i => !i.premium || i.premium && premium)
    .filter(i => i.location ? inLocation(context.profile.institution, i) : true)
  const sortedItems = chain(items)
    .sortBy(({order}) => order)
    .map((item) => ({...item, language, role}))

  const verticalItemCount = visibleItemCount(context.sizes, items.length)
  const verticalItems = menuItems(sortedItems.take(verticalItemCount), 'vertical', context.page, context)

  const verticalAnimation = useMemo(() => ({
    config: {delay: (context.menuShowing ? 0 : 250), duration: 400, easing: d3.easeQuad},
    to: {
      left: context.menuShowing ? 0 : -(itemWidth + 5)
    }
  }), [context.menuShowing])

  const horizontalItemCount = items.length - verticalItemCount
  const horizontalItems = menuItems(sortedItems.takeRight(horizontalItemCount), 'horizontal', context.page, context)

  const horizontalMenuTop = verticalItemCount * itemHeight + 100
  const horizontalMenuWidth = itemWidth * horizontalItemCount
  const horizontalAnimation = {
    to: {
      marginLeft: context.expandedMenuShowing ? itemWidth : -horizontalMenuWidth,
      top: horizontalMenuTop
    },
    from: {
      marginLeft: -9999,
    }
  }

  const onMoreMenuClick = () => {
    context.setState({expandedMenuShowing: !context.expandedMenuShowing})
  }

  return (
    <StyledMenu className="menu-container" sizes={context.sizes}>
      <Spring {...verticalAnimation}>
        { (props) => (
          <div id="menu" className='menu vertical' style={props}>
            {verticalItems}
            { horizontalItems.length > 0 && (
              <a id="more-menu" className="menu-item" onClick={onMoreMenuClick}>
                <Icon name="more-menu" />
              </a>
            )}
            <Box sx={{flexGrow: 1}} />
            <Version number={version} />
          </div>
        )}
      </Spring>
      <Spring {...horizontalAnimation}>
        {(props) =>
          (<div className="menu horizontal" style={props}>
            {horizontalItems}
          </div>
        )}
      </Spring>
    </StyledMenu>
  )
}


function menuStyles(props) {
  const {sizes, top = 100} = props
  const backgroundColor = '#0a4986'
  const selectedBackground = '#0f4071'
  const fontSize = '9pt'
  const itemSize = 90
  const height = sizes.height - top
  return {
    "& .menu": {
      display: 'flex',
      position: 'absolute',
      backgroundColor,
      "&.vertical": {
        zIndex: 3000,
        top,
        height: isNaN(height) ? 'calc(var(--screen-height, 100vh) - 100px)' : height,
        flexDirection: 'column'
      },
      "&.horizontal": {
        zIndex: 2900,
        backgroundColor,
        flexDirection: 'row'
      },
      "& .menu-item": {
        color: '#fff',
        cursor: 'pointer',
        backgroundColor,
        fontSize,
        textDecoration: 'none',
        width: itemSize,
        height: itemSize,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        "&:hover": {
          backgroundColor: '#2a4158'
        },
        "& label": {
          cursor: 'pointer',
          marginTop: 8
        },
        "&:active": {
          height: 88,
          borderBottom: '1px inset #246fb9',
          borderTop: '1px inset #246fb9',
          opacity: 0.5,
        },
        "&.selected": {
          backgroundColor: selectedBackground,
          "&:hover": { backgroundColor: selectedBackground},
          "&.vertical": {
              borderLeft: 'solid 4px #d6710f',
              width: 86
          },
          "&.selected.horizontal": {
            borderBottom: 'solid 4px #d6710f',
            height: 86
          },
          "& label": { marginLeft: -4},
          "& svg":  { marginLeft: -4}
        },
      }
    }
  }
}
